/**
 * DistributionVolumeChart - Gráfico premium de barras com volume de distribuições por hora
 * 
 * Design com gradiente, ícone decorativo, efeito shimmer e ChartContainer
 * 
 * Exibe distribuições das últimas 24h agrupadas por hora
 * Diferencia sucesso/falha com cores diferentes
 * 
 * @version 1.1.0
 * @date 2025-12-21
 */

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { BarChart3, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import { cn } from '@/lib/utils';
import type { HourlyVolume } from '@/hooks/useDistributionMonitor';

// ==============================================================
// Interfaces
// ==============================================================

interface DistributionVolumeChartProps {
  /** Dados de volume por hora */
  data: HourlyVolume[];
  /** Indica se está carregando */
  isLoading?: boolean;
}

// ==============================================================
// Configuração do Chart
// ==============================================================

const chartConfig = {
  sucesso: {
    label: 'Sucesso',
    color: 'hsl(142, 76%, 36%)',
  },
  falha: {
    label: 'Falha',
    color: 'hsl(0, 84%, 60%)',
  },
};

// ==============================================================
// Componente Principal
// ==============================================================

export function DistributionVolumeChart({ data, isLoading }: DistributionVolumeChartProps) {
  // Estado de loading com design premium
  if (isLoading) {
    return (
      <div className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-purple-500/5 to-purple-500/10 transition-all duration-300 hover:shadow-lg">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        
        <div className="relative flex items-center gap-3 p-4 border-b border-border/50">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-500/10">
            <BarChart3 className="h-5 w-5 text-purple-500" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Volume de Distribuições</h3>
            <p className="text-xs text-muted-foreground">Últimas 24 horas</p>
          </div>
        </div>
        
        <div className="p-4">
          <div className="h-[280px] bg-muted/50 animate-pulse rounded-lg" />
        </div>
      </div>
    );
  }

  // Formata dados para o gráfico (ordena por hora atual para trás)
  const currentHour = new Date().getHours();
  const sortedData = [...data].sort((a, b) => {
    const aOffset = a.hour <= currentHour ? a.hour + 24 : a.hour;
    const bOffset = b.hour <= currentHour ? b.hour + 24 : b.hour;
    return aOffset - bOffset;
  });

  const chartData = sortedData.map(item => ({
    name: `${item.hour.toString().padStart(2, '0')}h`,
    sucesso: item.successCount,
    falha: item.failureCount,
    total: item.count,
  }));

  // Calcula totais para badges
  const totalSuccess = chartData.reduce((sum, d) => sum + d.sucesso, 0);
  const totalFailure = chartData.reduce((sum, d) => sum + d.falha, 0);
  const hasData = chartData.some(d => d.total > 0);

  // Estado vazio
  if (!hasData) {
    return (
      <div className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-purple-500/5 to-purple-500/10 transition-all duration-300 hover:shadow-lg">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        
        <div className="relative flex items-center gap-3 p-4 border-b border-border/50">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-500/10">
            <BarChart3 className="h-5 w-5 text-purple-500" />
          </div>
          <h3 className="font-semibold text-foreground">Volume de Distribuições (24h)</h3>
        </div>
        
        <div className="p-12 text-center">
          <TrendingUp className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Nenhuma distribuição nas últimas 24 horas
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-purple-500/5 to-purple-500/10 transition-all duration-300 hover:shadow-lg">
      {/* Efeito shimmer no hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
      
      {/* Header premium */}
      <div className="relative flex items-center justify-between p-4 border-b border-border/50">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-500/10 transition-transform duration-300 group-hover:scale-110">
            <BarChart3 className="h-5 w-5 text-purple-500" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Volume de Distribuições</h3>
            <p className="text-xs text-muted-foreground">Últimas 24 horas por hora</p>
          </div>
        </div>
        
        {/* Badges de resumo */}
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
            ✓ {totalSuccess}
          </Badge>
          {totalFailure > 0 && (
            <Badge className="bg-destructive/10 text-destructive border-destructive/20">
              ✗ {totalFailure}
            </Badge>
          )}
        </div>
      </div>
      
      {/* Gráfico */}
      <div className="relative p-4">
        <ChartContainer config={chartConfig} className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid 
                strokeDasharray="3 3" 
                vertical={false}
                className="stroke-border/50" 
              />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
              />
              <ChartTooltip
                content={<ChartTooltipContent />}
                cursor={{ fill: 'hsl(var(--muted)/0.3)' }}
              />
              <ChartLegend content={<ChartLegendContent />} />
              <Bar 
                dataKey="sucesso" 
                stackId="a" 
                fill="hsl(142, 76%, 36%)" 
                radius={[0, 0, 0, 0]}
                className="transition-all duration-200 hover:opacity-80"
              />
              <Bar 
                dataKey="falha" 
                stackId="a" 
                fill="hsl(0, 84%, 60%)" 
                radius={[4, 4, 0, 0]}
                className="transition-all duration-200 hover:opacity-80"
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </div>
    </div>
  );
}
